import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  const navigationItems = [
    { path: '/homepage', label: 'Home', icon: 'Home' },
    { path: '/portfolio', label: 'Portfolio', icon: 'Briefcase' },
    { path: '/about', label: 'About', icon: 'User' },
    { path: '/services', label: 'Services', icon: 'Settings' },
    { path: '/contact', label: 'Contact', icon: 'Mail' }
  ];

  const isActivePath = (path) => location?.pathname === path;

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-40 bg-card shadow-md">
        <div className="flex items-center justify-between h-16 px-2 sm:px-4 lg:px-8 gap-x-2 sm:gap-x-4">
          <Link 
            to="/homepage" 
            className="flex items-center gap-x-2 sm:gap-x-3 md:gap-x-4 transition-transform duration-300 hover:scale-105"
            style={{ minHeight: '56px' }}
          >
            {/* Icon: hidden on desktop, visible and larger on mobile */}
            <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-primary/10 lg:hidden">
              <Icon name="Code2" size={32} color="var(--color-primary)" />
            </div>
            {/* Image: always visible */}
            <img
              src="/assets/images/my-pic6.png"
              alt="Mr Mike Technologies"
              className="w-10 h-10 sm:w-11 sm:h-11 md:w-12 md:h-12 rounded-full object-cover border-2 border-primary shadow-sm"
              style={{ marginTop: '0px' }}
            />
            <span className="text-base sm:text-lg md:text-xl font-bold font-heading text-foreground whitespace-nowrap">
              Mr Mike Technologies
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {navigationItems?.map((item) => (
              <Link
                key={item?.path}
                to={item?.path}
                className={`
                  flex items-center gap-2 px-3 py-2 rounded-lg font-medium transition-all duration-300
                  ${isActivePath(item?.path)
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }
                `}
              >
                <Icon name={item?.icon} size={18} />
                <span>{item?.label}</span>
              </Link>
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center gap-2 sm:gap-3">
            <Link
              to="/technical-blog"
              className="flex items-center gap-2 px-3 py-2 rounded-lg font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-300"
            >
              <Icon name="BookOpen" size={18} />
              <span>Blog</span>
            </Link>
            <Link
              to="/contact"
              className="px-4 py-2 rounded-lg font-cta font-bold bg-primary text-primary-foreground hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300"
            >
              Start Project
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMobileMenu}
            className="lg:hidden p-2 rounded-lg text-foreground hover:bg-muted transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-primary"
            aria-label="Toggle mobile menu"
          >
            <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={28} />
          </button>
        </div>
      </header>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          {/* Overlay */}
          <div 
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={toggleMobileMenu}
          />
          {/* Slide-in Menu */}
          <div className="absolute top-0 right-0 w-full max-w-xs sm:max-w-sm h-full bg-card shadow-lg animate-slide-in-right">
            <div className="flex items-center justify-between h-16 px-4 border-b border-border">
              <Link 
                to="/homepage" 
                className="flex items-center gap-x-2"
                style={{ minHeight: '56px' }}
              >
                {/* Icon: visible and larger on mobile menu */}
                <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-primary/10">
                  <Icon name="Code2" size={32} color="var(--color-primary)" />
                </div>
                <img
                  src="/assets/images/my-pic6.png"
                  alt="Mr Mike Technologies"
                  className="w-10 h-10 rounded-full object-cover border-2 border-primary shadow-sm"
                  style={{ marginTop: '0px' }}
                />
              </Link>
              <button
                onClick={toggleMobileMenu}
                className="p-2 rounded-lg text-foreground hover:bg-muted transition-colors duration-300"
                aria-label="Close mobile menu"
              >
                <Icon name="X" size={24} />
              </button>
            </div>
            <nav className="flex flex-col p-4 gap-2">
              {navigationItems?.map((item) => (
                <Link
                  key={item?.path}
                  to={item?.path}
                  onClick={toggleMobileMenu}
                  className={`
                    flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-all duration-300
                    ${isActivePath(item?.path)
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }
                  `}
                >
                  <Icon name={item?.icon} size={20} />
                  <span>{item?.label}</span>
                </Link>
              ))}
              <Link
                to="/technical-blog"
                onClick={toggleMobileMenu}
                className={`
                  flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-all duration-300
                  ${isActivePath('/technical-blog')
                    ? 'bg-primary text-primary-foreground' :'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }
                `}
              >
                <Icon name="BookOpen" size={20} />
                <span>Blog</span>
              </Link>
              <div className="mt-4 pt-4 border-t border-border">
                <Link
                  to="/contact"
                  onClick={toggleMobileMenu}
                  className="flex items-center justify-center gap-2 w-full px-6 py-3 rounded-lg font-cta font-bold bg-primary text-primary-foreground hover:shadow-lg transition-all duration-300"
                >
                  <Icon name="Rocket" size={20} />
                  <span>Start Project</span>
                </Link>
              </div>
            </nav>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;